import { z } from 'zod';
import {
  BILL_AGES,
  BILL_REASON_MAX,
  CREDIT_KINDS,
  CREDIT_NOTE_MAX,
  PAYMENT_METHODS,
  PAYMENT_NOTE_MAX,
  PAYMENT_REFERENCE_MAX,
  RECEIVED_METHODS,
} from '../../src/domain/finance/caseBill';
import {
  CalendarDateSchema,
  CaseReleaseSchema,
  IdSchema,
  InstantSchema,
  JobOrderNoSchema,
  MoneySchema,
  OmittedIdentitySchema,
} from './common.schema';
import { DeliverToSchema } from './geo.schema';
import {
  AdjustmentKindSchema,
  AdjustmentModeSchema,
  BillAdjustmentSchema,
  PaymentStatusSchema,
  ReceiptLineSchema,
} from './receipt.schema';

/**
 * The money of a case beyond its receipt. The request of 8 October 2026 (fourth):
 * the payment summary of a case, its discounts and charges, its payments one at a
 * time, a doctor's credit and statement, and the overview of what the lab is owed.
 * The rules are the domain's `caseBill.ts`, which the API's `CaseBill` mirrors.
 */

function enumOf<T extends string>(values: readonly T[]) {
  return z.enum(values as unknown as [T, ...T[]]);
}

export const CreditKindSchema = enumOf(CREDIT_KINDS);
export const BillAgeSchema = enumOf(BILL_AGES);

/**
 * How a payment came, as the server sends it: one of cash, transfer, cheque, card,
 * or credit for the doctor's credit used on the case; null when nobody said. Text,
 * and not the enumeration, because a record from before methods were chosen from a
 * list keeps whatever was written on it.
 */
export const PaymentMethodValueSchema = z.string().nullable();

/** One payment on a case. `id` is `recorded` for the one payment of a case paid before payments were kept one by one. */
export const CasePaymentSchema = z.object({
  id: IdSchema,
  amount: MoneySchema,
  method: PaymentMethodValueSchema,
  reference: z.string().nullable(),
  note: z.string().nullable(),
  /** When the money came, which the day it was entered need not be. */
  paidAt: InstantSchema,
  byName: z.string().nullable(),
});

/** One row of what was done to a case's money, from the activity log, newest first. */
export const PaymentHistoryEntrySchema = z.object({
  at: InstantSchema,
  byName: z.string().nullable(),
  action: z.string(),
});

export const PaymentSummarySchema = z.object({
  jobOrderNo: JobOrderNoSchema,
  /** The number on the doctor's paper, which an invoice quotes. The request of 17 October 2026, item 7. */
  serialNumber: z.string().nullable().optional(),
  /** False for a remake the lab does not charge for. */
  chargeable: z.boolean(),
  createdAt: InstantSchema,
  deliveryDate: CalendarDateSchema,
  /** The doctor's record, or null for a case that was never linked to one. */
  doctorId: IdSchema.nullable(),
  doctorCode: z.string().nullable(),
  /** Identity: absent without seeIdentity. */
  doctorName: OmittedIdentitySchema,
  patientName: OmittedIdentitySchema,
  lines: z.array(ReceiptLineSchema),
  /** What the lines come to. */
  subtotal: MoneySchema,
  adjustments: z.array(BillAdjustmentSchema),
  /** Discounts and charges together, signed. */
  adjustmentsTotal: z.number(),
  /** What the case owes after them. Never below zero. */
  due: MoneySchema,
  payments: z.array(CasePaymentSchema),
  paidAmount: MoneySchema,
  balance: MoneySchema,
  status: PaymentStatusSchema,
  /** When the case became paid, or null while it is not. */
  paidAt: InstantSchema.nullable(),
  /**
   * Released to be shipped unpaid, and whether it has left: what Release and
   * its taking back are offered by. The request of 17 October 2026, item 8.
   * Absent from an API older than they are.
   */
  release: CaseReleaseSchema.nullable().optional(),
  dispatched: z.boolean().optional(),
  /** Whom an invoice is to: identity, absent without seeIdentity. Item 12 of the same request. */
  deliverTo: DeliverToSchema.nullable().optional(),
  /** The doctor's credit that could be used on this case, or null for a case with no doctor's record. */
  credit: MoneySchema.nullable(),
  history: z.array(PaymentHistoryEntrySchema),
});

/** `POST /job-orders/{jo}/adjustments`. */
export const AddAdjustmentSchema = z.object({
  kind: AdjustmentKindSchema,
  mode: AdjustmentModeSchema,
  value: z.number().positive(),
  reason: z.string().trim().min(1).max(BILL_REASON_MAX),
});

/**
 * `POST /job-orders/{jo}/payments`. `method` credit takes the amount from the
 * doctor's credit. `paidOn` is the lab's day the money came, left off for today.
 */
export const AddPaymentSchema = z.object({
  amount: z.number().positive(),
  method: enumOf(PAYMENT_METHODS).nullable().optional(),
  reference: z.string().max(PAYMENT_REFERENCE_MAX).nullable().optional(),
  note: z.string().max(PAYMENT_NOTE_MAX).nullable().optional(),
  paidOn: CalendarDateSchema.optional(),
});

/** `PUT /job-orders/{jo}/payments/{id}`: a payment put right. Never one made from credit. */
export const EditPaymentSchema = z.object({
  amount: z.number().positive().optional(),
  method: enumOf(RECEIVED_METHODS).nullable().optional(),
  reference: z.string().max(PAYMENT_REFERENCE_MAX).nullable().optional(),
  note: z.string().max(PAYMENT_NOTE_MAX).nullable().optional(),
  paidOn: CalendarDateSchema.optional(),
});

/** One movement of a doctor's credit. `amount` is signed: more than zero adds to the balance. */
export const CreditEntrySchema = z.object({
  id: IdSchema,
  amount: z.number(),
  kind: CreditKindSchema,
  /** The case it was used on or came back from. */
  jobOrderNo: JobOrderNoSchema.nullable(),
  method: PaymentMethodValueSchema,
  reference: z.string().nullable(),
  note: z.string().nullable(),
  byName: z.string().nullable(),
  at: InstantSchema,
});

/** A doctor's credit with the lab: the balance, which is the sum of the entries, and each of them, newest first. */
export const DoctorCreditSchema = z.object({
  balance: MoneySchema,
  entries: z.array(CreditEntrySchema),
});

/** `POST /doctors/{id}/credit`. */
export const AddCreditSchema = z.object({
  amount: z.number().positive(),
  note: z.string().trim().min(1).max(CREDIT_NOTE_MAX),
  method: enumOf(RECEIVED_METHODS).nullable().optional(),
  reference: z.string().max(PAYMENT_REFERENCE_MAX).nullable().optional(),
});

/** `POST /doctors/{id}/credit/remove`. */
export const RemoveCreditSchema = z.object({
  amount: z.number().positive(),
  note: z.string().trim().min(1).max(CREDIT_NOTE_MAX),
});

export const StatementCaseSchema = z.object({
  jobOrderNo: JobOrderNoSchema,
  patientName: OmittedIdentitySchema,
  createdAt: InstantSchema,
  deliveryDate: CalendarDateSchema,
  due: MoneySchema,
  paidAmount: MoneySchema,
  /** Nothing for an archived case: it is off every list of work in hand. */
  balance: MoneySchema,
  status: PaymentStatusSchema,
  /** Days since the lab's day it was booked. */
  ageDays: z.number().int().nonnegative(),
  dispatched: z.boolean(),
  archived: z.boolean(),
});

/** One doctor's cases as bills, newest first, what they come to together, and the doctor's credit. */
export const DoctorStatementSchema = z.object({
  doctorId: IdSchema,
  code: z.string(),
  name: OmittedIdentitySchema,
  clinic: OmittedIdentitySchema,
  caseCount: z.number().int().nonnegative(),
  billed: MoneySchema,
  paid: MoneySchema,
  outstanding: MoneySchema,
  /** The cases that still owe something. */
  openCases: z.number().int().nonnegative(),
  credit: DoctorCreditSchema,
  cases: z.array(StatementCaseSchema),
});

const OwedSchema = z.object({
  cases: z.number().int().nonnegative(),
  outstanding: MoneySchema,
});

/** What the lab is owed: every case that is not paid, not archived and owes something. */
export const FinanceOverviewSchema = z.object({
  /** The lab's day the ages are counted to. */
  asOf: CalendarDateSchema,
  outstanding: MoneySchema,
  cases: z.number().int().nonnegative(),
  partPaid: z.object({
    cases: z.number().int().nonnegative(),
    /** What has been received on them so far. */
    received: MoneySchema,
  }),
  /** The credit doctors hold with the lab, all of it. */
  creditHeld: MoneySchema,
  /** By the days since each case was booked: 0-30, 31-60, over60, in that order. */
  ages: z.array(OwedSchema.extend({ key: BillAgeSchema })),
  /** Most owed first. A doctor who owes nothing and holds credit is listed too. */
  doctors: z.array(
    OwedSchema.extend({
      doctorId: IdSchema,
      code: z.string(),
      name: OmittedIdentitySchema,
      clinic: OmittedIdentitySchema,
      /** The age of the doctor's oldest open case, in days. */
      oldestDays: z.number().int().nonnegative(),
      credit: MoneySchema,
    }),
  ),
  /** The open cases with no doctor's record behind them. */
  unlinked: OwedSchema,
});

/**
 * One item of the price list, with its price for everyone and this doctor's
 * own where Accounts set one. The lab's request of 17 October 2026, item 11.
 */
export const DoctorItemPriceSchema = z.object({
  itemId: IdSchema,
  name: z.string(),
  category: z.string(),
  kind: z.enum(['unit', 'span']),
  active: z.boolean(),
  listPrice: MoneySchema,
  doctorPrice: MoneySchema.nullable(),
  /** When the doctor's own price was last set, or null for none. */
  updatedAt: InstantSchema.nullable(),
});

/**
 * `GET /doctors/{id}/prices`: every active item of the price list, and a
 * retired one while the doctor still has a price for it, in the price list's
 * order. `name` is identity: absent without seeIdentity.
 */
export const DoctorPriceListSchema = z.object({
  doctorId: IdSchema,
  code: z.string(),
  name: OmittedIdentitySchema,
  items: z.array(DoctorItemPriceSchema),
});

/** `PUT /doctors/{id}/prices/{itemId}`. */
export const SetDoctorPriceSchema = z.object({
  unitPrice: z.number(),
});

/**
 * `GET /finance/settings`: the system's currency, the rate to read it in
 * dollars and who set it last, and the letterhead an invoice is headed with.
 * The lab's request of 17 October 2026, items 12 and 13. `usdRate` is how many
 * dirhams one dollar buys, null until Accounts set one. `lab.phone` and
 * `lab.email` are blank while they are the placeholders the settings were
 * seeded with, and an invoice prints neither while it is blank.
 */
export const FinanceSettingsSchema = z.object({
  currency: z.string(),
  foreign: z.string(),
  usdRate: z.number().positive().nullable(),
  usdRateSetAt: InstantSchema.nullable(),
  usdRateSetByName: z.string().nullable(),
  lab: z.object({
    name: z.string(),
    address: z.string(),
    phone: z.string(),
    email: z.string(),
  }),
});

/** `PUT /finance/exchange-rate`. */
export const SetExchangeRateSchema = z.object({
  usdRate: z.number(),
});

export type PaymentSummary = z.infer<typeof PaymentSummarySchema>;
export type DoctorPriceList = z.infer<typeof DoctorPriceListSchema>;
export type FinanceSettings = z.infer<typeof FinanceSettingsSchema>;
export type CasePaymentRow = z.infer<typeof CasePaymentSchema>;
export type DoctorCreditView = z.infer<typeof DoctorCreditSchema>;
export type CreditEntryRow = z.infer<typeof CreditEntrySchema>;
export type DoctorStatement = z.infer<typeof DoctorStatementSchema>;
export type StatementCase = z.infer<typeof StatementCaseSchema>;
export type FinanceOverview = z.infer<typeof FinanceOverviewSchema>;
